/**
 * Client share portal: routing, image sources and what a room page shows.
 *
 * Pulled out of `views/share-view.ts` so the decisions are testable; the view
 * only renders what these return.
 *
 * Routes stay inside the share link: `#/share/<token>` is the overview and
 * `#/share/<token>/room/<roomId>` is a room page. main.ts reads the token with
 * `[^/?#]+`, so the room suffix never changes which project is loaded, and
 * every link already sent to a client keeps working unchanged.
 */

import { esc } from '@/lib/dom';
import type { SharedProject } from '@/lib/data/share';

export interface ShareRoute {
  token: string;
  /** null on the overview. */
  roomId: string | null;
}

export function parseShareRoute(hash: string): ShareRoute | null {
  const m = /^#\/share\/([^/?#]+)(?:\/room\/([^/?#]+))?\/?(?:[?#].*)?$/.exec(hash);
  if (!m) {
    // An unrecognized suffix still belongs to this share link — show the
    // overview rather than nothing, as main.ts would.
    const t = /^#\/share\/([^/?#]+)/.exec(hash);
    return t ? { token: safeDecode(t[1]!), roomId: null } : null;
  }
  return { token: safeDecode(m[1]!), roomId: m[2] ? safeDecode(m[2]) : null };
}

export function shareHref(token: string, roomId: string | null = null): string {
  const base = `#/share/${encodeURIComponent(token)}`;
  return roomId ? `${base}/room/${encodeURIComponent(roomId)}` : base;
}

function safeDecode(s: string): string {
  try { return decodeURIComponent(s); } catch { return s; }
}

// ─── Images ────────────────────────────────────────────────────────────────

export type ImageSource =
  | { kind: 'url'; url: string }
  | { kind: 'storage'; path: string };

/**
 * Cover photos are stored two ways: an uploaded file's storage path
 * (`<project uuid>/hero/…`), or — for older rows — a pasted http(s) URL.
 * The portal used to render only the second, so every uploaded cover photo
 * was silently skipped.
 */
export function imageSource(value: string | null | undefined): ImageSource | null {
  const v = (value ?? '').trim();
  if (!v) return null;
  if (/^https?:\/\//i.test(v)) return { kind: 'url', url: v };
  // Anything else with a scheme (javascript:, data:, …) is not a storage path.
  if (/^[a-z][a-z0-9+.-]*:/i.test(v)) return null;
  return { kind: 'storage', path: v };
}

/**
 * An <img> for a stored image value, or '' when there is none. Storage paths
 * go in `data-storage-path` for the view to sign; they are never usable as a
 * `src` directly.
 */
export function mediaImg(value: string | null | undefined, opts: { className?: string; alt?: string } = {}): string {
  const src = imageSource(value);
  if (!src) return '';
  const cls = opts.className ? ` class="${esc(opts.className)}"` : '';
  const alt = ` alt="${esc(opts.alt ?? '')}"`;
  return src.kind === 'url'
    ? `<img${cls} src="${esc(src.url)}"${alt} />`
    : `<img${cls} data-storage-path="${esc(src.path)}"${alt} />`;
}

type SharedImage = SharedProject['room_images'][number];

export interface GroupedRoomImages {
  /** Before/after matched in sort order. */
  pairs: Array<[SharedImage, SharedImage]>;
  /** Befores or afters left over once pairs run out — still shown, never dropped. */
  unpaired: SharedImage[];
  inspiration: SharedImage[];
  /** `in_progress` — a kind the enum has had since 0001; no gallery showed it. */
  inProgress: SharedImage[];
  detail: SharedImage[];
  total: number;
}

export function groupRoomImages(images: SharedImage[], roomId: string): GroupedRoomImages {
  const mine = images
    .filter((i) => i.room_id === roomId)
    .slice()
    .sort((a, b) => a.sort_order - b.sort_order);
  const before = mine.filter((i) => i.kind === 'before');
  const after = mine.filter((i) => i.kind === 'after');
  const n = Math.min(before.length, after.length);
  const pairs: Array<[SharedImage, SharedImage]> = [];
  for (let i = 0; i < n; i++) pairs.push([before[i]!, after[i]!]);
  return {
    pairs,
    unpaired: [...before.slice(n), ...after.slice(n)],
    inspiration: mine.filter((i) => i.kind === 'inspiration'),
    inProgress: mine.filter((i) => i.kind === 'in_progress'),
    detail: mine.filter((i) => i.kind === 'detail'),
    total: mine.length,
  };
}

// ─── Rooms ─────────────────────────────────────────────────────────────────

type SharedRoom = SharedProject['rooms'][number];

export function roomsByLocation(rooms: SharedRoom[]): { interior: SharedRoom[]; exterior: SharedRoom[] } {
  return {
    interior: rooms.filter((r) => !r.is_exterior),
    exterior: rooms.filter((r) => r.is_exterior),
  };
}

export interface RoomPage {
  room: SharedRoom;
  specs: SharedProject['specs'];
  images: GroupedRoomImages;
  palette: SharedProject['palette'];
  schedule: SharedProject['schedule'];
  openFeedback: SharedProject['room_feedback'];
  /** True when there is nothing to show beyond the header. */
  isEmpty: boolean;
}

/**
 * Everything the room page shows, or null when the id isn't a room in this
 * payload — a deleted room, or an id pasted from another project. The payload
 * is already scoped to the share token server-side; this never widens it.
 */
export function roomPage(d: SharedProject, roomId: string): RoomPage | null {
  const room = d.rooms.find((r) => r.id === roomId);
  if (!room) return null;
  const specs = d.specs
    .filter((s) => s.room_id === roomId)
    .slice()
    .sort((a, b) => a.sort_order - b.sort_order);
  const images = groupRoomImages(d.room_images, roomId);
  const palette = d.palette
    .filter((c) => c.room_id === roomId)
    .slice()
    .sort((a, b) => a.sort_order - b.sort_order);
  const schedule = d.schedule.filter((e) => e.room_id === roomId);
  const openFeedback = d.room_feedback.filter((f) => f.room_id === roomId && !f.resolved_at);
  return {
    room, specs, images, palette, schedule, openFeedback,
    isEmpty: specs.length === 0 && images.total === 0 && palette.length === 0,
  };
}
